/**
 * Playing a ghost.
 *
 * The point of this file is that a ghost must not sound like a different kind of thing from the
 * rest of the band. A fragment that arrived over a WebSocket and a signal synthesised locally
 * both come out of the same receiver, so both go through the same filter chain: band-passed,
 * rolled off, sitting under the static rather than on top of it. If a ghost played clean while
 * the scripted voices played filtered, a player would learn to tell them apart in about ten
 * seconds and the entire premise — *is that a person?* — would be gone.
 *
 * It plays on the mixer's `ghost` bus, which Chapter 1 staged deliberately low.
 */
import { audioContext, rampTo } from './context.js';
import type { Mixer } from './mixer.js';

/** Ghosts sit a little further down the band than a story signal: heard, not addressed. */
const GHOST_BANDPASS_HZ = 1150;
const GHOST_BANDPASS_Q = 2.2;
const GHOST_LOWPASS_HZ = 2600;
const GHOST_HIGHPASS_HZ = 320;

export interface GhostPlayerOptions {
  mixer: Mixer;
}

export class GhostPlayer {
  private readonly mixer: Mixer;
  private muted = false;
  private playing = 0;

  constructor(options: GhostPlayerOptions) {
    this.mixer = options.mixer;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.mixer.setBusMuted('ghost', muted, 0.2);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * Decode and play one fragment.
   *
   * The fragment's declared mime type is not needed: `decodeAudioData` determines the container
   * from the bytes, and trusting the sender's label over the actual content would be the wrong
   * way round anyway.
   *
   * Failure is silent by design: a fragment in a codec this browser cannot decode, or a truncated
   * payload, means one ghost nobody hears. There is no correct way to tell a player about that.
   */
  async play(base64Audio: string): Promise<boolean> {
    if (this.muted) return false;
    // A cap on concurrency: two voices at once reads as a crowd, and a crowd is not a haunting.
    if (this.playing >= 2) return false;

    const ctx = audioContext();
    if (ctx.state !== 'running') return false;

    let buffer: AudioBuffer;
    try {
      buffer = await ctx.decodeAudioData(decodeBase64(base64Audio));
    } catch {
      return false;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    // The same shape as a distant transmission in the tuning chain, so a ghost and a scripted
    // voice arrive through the same receiver.
    const highPass = ctx.createBiquadFilter();
    highPass.type = 'highpass';
    highPass.frequency.value = GHOST_HIGHPASS_HZ;

    const bandPass = ctx.createBiquadFilter();
    bandPass.type = 'bandpass';
    bandPass.frequency.value = GHOST_BANDPASS_HZ;
    bandPass.Q.value = GHOST_BANDPASS_Q;

    const lowPass = ctx.createBiquadFilter();
    lowPass.type = 'lowpass';
    lowPass.frequency.value = GHOST_LOWPASS_HZ;

    const gain = ctx.createGain();
    gain.gain.value = 0;

    source.connect(highPass).connect(bandPass).connect(lowPass).connect(gain);
    gain.connect(this.mixer.bus('ghost'));

    // Fade in and out rather than cutting: a fragment that starts abruptly reads as a sound
    // effect, one that swims up out of the noise reads as someone drifting into range.
    const now = ctx.currentTime;
    const duration = buffer.duration;
    const edge = Math.min(0.35, duration / 3);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1, now + edge);
    gain.gain.setValueAtTime(1, now + Math.max(duration - edge, edge));
    gain.gain.linearRampToValueAtTime(0, now + duration);

    this.playing += 1;
    source.start(now);
    source.stop(now + duration + 0.05);
    source.onended = () => {
      this.playing -= 1;
      gain.disconnect();
    };

    return true;
  }

  /** Silence anything currently playing — used when the player mutes mid-fragment. */
  stopAll(): void {
    rampTo(this.mixer.bus('ghost').gain, this.muted ? 0 : 1, 0.1);
  }
}

/** Base64 → ArrayBuffer, without pulling in a dependency. */
function decodeBase64(value: string): ArrayBuffer {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
